// firebase/config.ts
// Firebase initialization and configuration

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import type { FirebaseConfig } from '../types';

// FIREBASE_ENV (baked in at build time — see package.json's `build` script)
// selects which backend this client talks to: 'development' connects to the
// local Firebase Emulator Suite, 'staging'/'production' use real projects
// via the REACT_APP_FIREBASE_* values injected at build time.
const FIREBASE_ENV = process.env.REACT_APP_FIREBASE_ENV || 'production';

const getRequiredEnvVar = (key: string): string => {
    const value = process.env[key];
    if (!value || value.trim() === '') {
        throw new Error(
            `Missing required environment variable: ${key}\n` +
            `Please ensure all Firebase configuration variables are set in your .env file.\n` +
            `See .env.example for required variables.`
        );
    }
    return value;
};

const firebaseConfig: FirebaseConfig = {
    apiKey: getRequiredEnvVar('REACT_APP_FIREBASE_API_KEY'),
    authDomain: getRequiredEnvVar('REACT_APP_FIREBASE_AUTH_DOMAIN'),
    projectId: getRequiredEnvVar('REACT_APP_FIREBASE_PROJECT_ID'),
    storageBucket: getRequiredEnvVar('REACT_APP_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getRequiredEnvVar('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getRequiredEnvVar('REACT_APP_FIREBASE_APP_ID'),
};

const app = initializeApp(firebaseConfig);

// App Check — identifies this web client as the admin_panel to backend attestation.
// onAuthCreate reads request.app to set clients: ['admin_panel'] in the token.
// Skipped against the local emulator suite: there's no real backend to attest
// to, and the debug-token exchange needs to reach Google's real servers,
// which just adds an external network dependency to local dev and smoke tests.
export const appCheck =
    FIREBASE_ENV === 'development'
        ? undefined
        : initializeAppCheck(app, {
              provider: new ReCaptchaV3Provider(
                  getRequiredEnvVar('REACT_APP_FIREBASE_APPCHECK_KEY')
              ),
              isTokenAutoRefreshEnabled: true,
          });

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

if (FIREBASE_ENV === 'development') {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
}

export default app;
